import { Component, Input, Output, EventEmitter, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DroneResourceService } from '../../../core/data-services/drone-resource.service';
import { DroneResourceUpload } from '../../../core/request-models/drone-resource-upload.model';
import { FileUploadService } from '../../../core/api-services/file-upload.service';
import { RemoveDashPipe } from '../../pipes/remove-dash.pipe';
import { DroneResource } from '../../../core/data-models/drone-resource.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/** Mirrors DRONE_RESOURCE_TYPES in the admin portal and the Hub. */
export const DRONE_RESOURCE_TYPES = [
  'registration',
  'insurance',
  'manual',
  'invoice/proof-of-purchase',
  'maintenance-documentation',
  'other'
];

/**
 * Copy of the admin portal's DroneResourceUploadModalComponent, minus the
 * Access switch — operator uploads are always registered as shared, and minus
 * edit mode, which is admin-only.
 */
@Component({
  selector: 'app-drone-resource-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RemoveDashPipe, MaxLengthDirective],
  templateUrl: './drone-resource-modal.component.html',
  styleUrls: ['./drone-resource-modal.component.scss']
})
export class DroneResourceModalComponent {
  private fb = inject(FormBuilder);
  private droneResourceService = inject(DroneResourceService);
  private fileUploadService = inject(FileUploadService);

  @Input() droneId: string = '';
  @Input() droneName: string = '';

  @Output() newItem = new EventEmitter<DroneResource>();
  @Output() close = new EventEmitter<void>();

  resourceForm: FormGroup;

  selectedFile = signal<File | null>(null);
  uploading = signal(false);
  errorMessage = signal('');

  resourceTypes = DRONE_RESOURCE_TYPES;

  constructor() {
    this.resourceForm = this.fb.group({
      resourceType: ['', Validators.required],
      otherType: [''],
      caption: ['']
    });

    this.resourceForm.get('otherType')!.disable({ emitEvent: false });

    this.resourceForm.get('resourceType')!.valueChanges.subscribe(value => {
      const otherType = this.resourceForm.get('otherType')!;

      if (value === 'other') {
        otherType.setValidators([Validators.required]);
        otherType.enable({ emitEvent: false });
      } else {
        otherType.clearValidators();
        otherType.setValue('', { emitEvent: false });
        otherType.disable({ emitEvent: false });
      }

      otherType.updateValueAndValidity({ emitEvent: false });
    });
  }

  get canSubmit(): boolean {
    return this.resourceForm.valid && !!this.selectedFile() && !this.uploading();
  }

  get effectiveResourceType(): string {
    const { resourceType, otherType } = this.resourceForm.getRawValue();
    return resourceType === 'other' ? (otherType ?? '').trim() : resourceType;
  }

  formatFileSize(bytes: number): string {
    return this.fileUploadService.formatFileSize(bytes);
  }

  showError(controlName: string): boolean {
    const control = this.resourceForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(): void {
    if (!this.uploading()) {
      this.onClose();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const maxSize = this.fileUploadService.getMaxFileSize();
      if (file.size > maxSize) {
        this.errorMessage.set(
          `File is too large (${this.formatFileSize(file.size)}). Max is ${this.formatFileSize(maxSize)}.`
        );
        this.selectedFile.set(null);
        input.value = '';
        return;
      }
      this.selectedFile.set(file);
      this.errorMessage.set('');
    }
  }

  get maxFileSizeLabel(): string {
    return this.fileUploadService.formatFileSize(this.fileUploadService.getMaxFileSize());
  }

  async onSubmit(): Promise<void> {
    this.resourceForm.markAllAsTouched();

    if (!this.selectedFile()) {
      this.errorMessage.set('Choose a file to upload.');
      return;
    }

    if (!this.canSubmit) {
      this.errorMessage.set('Fill in the required fields before submitting.');
      return;
    }

    await this.uploadFile();
  }

  async uploadFile(): Promise<boolean> {
    const file = this.selectedFile();

    if (!this.droneId) {
      this.errorMessage.set('No drone selected.');
      return false;
    }

    if (file === null) {
      this.errorMessage.set('Choose a file to upload.');
      return false;
    }

    this.uploading.set(true);
    this.errorMessage.set('');

    const meta: DroneResourceUpload = {
      resourceType: this.effectiveResourceType,
      caption: this.resourceForm.get('caption')!.value || undefined
    };

    try {
      const response = await this.droneResourceService.uploadResourceByDroneId(
        this.droneId,
        file,
        meta
      );
      this.newItem.emit(response);
      return true;
    } catch (error: any) {
      console.error('Could not upload resource', error);
      this.errorMessage.set(error?.message || 'Could not upload resource.');
      return false;
    } finally {
      this.uploading.set(false);
    }
  }
}
